if (!customElements.get('campaigns-element')) {
  customElements.define(
    'campaigns-element',
    class CampaignsElement extends HTMLElement {
      // Cross-instance leader. Only the worker mutates the cart
      // (addToCart / updateCart). Non-worker instances still refresh
      // their own innerHTML so the drawer/PDP block UI stays in sync.
      static activeWorker = null;

      constructor() {
        super();
        this.instanceId = Math.random().toString(36).slice(2);
        this.sectionId = this.dataset.sectionId;
        this.campaigns = {};
        this.activeCampaign = null;
        this.cartUpdated = false;
        this.cartUpdateFailure = false;
        this.prevCampaigns = this.querySelectorAll('.campaign-block');
        this.cart = document.querySelector('cart-notification') || document.querySelector('drawer-cart');
        this.inCart = window.location.pathname === '/cart';
        this.updateInProgress = false;
        this.updateQueue = [];
        this.debounceTimer = null;

        // Bind event handlers to preserve context
        this.handleAjaxProductAdded = this.onCartUpdate.bind(this);
        this.handleCartUpdate = this.onCartUpdate.bind(this);

        // Claim leadership eagerly so the initial checkCart only runs once
        // even when multiple instances co-exist (drawer + PDP block).
        if (!CampaignsElement.activeWorker) {
          CampaignsElement.activeWorker = this;
        }

        if (this.isWorker) {
          this.checkCart();
        }
      }

      connectedCallback() {
        // Re-claim leadership if the previous leader was disconnected
        // before this instance was attached.
        if (!CampaignsElement.activeWorker) {
          CampaignsElement.activeWorker = this;
        }
        document.addEventListener('ajaxProduct:added', this.handleAjaxProductAdded);
        document.addEventListener('cart:update', this.handleCartUpdate);
      }

      disconnectedCallback() {
        document.removeEventListener('ajaxProduct:added', this.handleAjaxProductAdded);
        document.removeEventListener('cart:update', this.handleCartUpdate);
        if (CampaignsElement.activeWorker === this) {
          const others = document.querySelectorAll('campaigns-element');
          const next = Array.from(others).find((el) => el !== this);
          CampaignsElement.activeWorker = next || null;
        }
      }

      get isWorker() {
        return CampaignsElement.activeWorker === this;
      }

      /**
       * Handles cart update events
       * @param {Event} event - The cart update event
       */
      async onCartUpdate(event) {
        // Optimisation only — skip events this instance just dispatched.
        // Correctness does not depend on this gate: the leader refreshes
        // its own innerHTML inside processCartUpdate (both before and
        // after its cart mutations), so it does not need a second pass
        // triggered by its own event. Skipping it just avoids a wasted
        // section fetch and a brief checkout-button flicker. Other
        // instances always process the event because their instanceId
        // differs, so their UI still stays in sync.
        if (event?.detail?.from === this.instanceId) {
          return;
        }
        this.debounce(() => this.processCartUpdate());
      }

      /**
       * Debounces function calls
       * @param {Function} callback - Function to call after debounce delay
       * @param {number} delay - Delay in ms
       */
      debounce(callback, delay = 300) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          callback();
        }, delay);
      }

      /**
       * Processes cart updates with loading state management
       */
      async processCartUpdate() {
        if (this.updateInProgress) {
          return;
        }

        // Only the leader locks the checkout buttons, because only the
        // leader will run removeIneligibleGifts (which is responsible
        // for re-enabling them). Non-leaders just refresh innerHTML.
        if (this.isWorker) {
          window.campaignsProcessing = true;
          document.querySelectorAll('button[type="submit"][form="cart"], shopify-paypal-button, shopify-apple-pay-button').forEach((button) => {
            button.disabled = true;
          });
        }

        try {
          this.updateInProgress = true;

          const refreshed = await this.refreshSectionHTML();
          if (!refreshed) return;

          // Only the leader mutates the cart. With multiple instances
          // (drawer + PDP block) on a PDP, running these on every
          // instance caused a 1 → 2 → 1 gift-quantity bounce.
          if (this.isWorker) {
            await this.removeIneligibleGifts();

            if (!this.cartUpdateFailure) {
              await this.checkCart();
            }

            // The leader just mutated the cart, but its own innerHTML
            // is still showing the pre-mutation render captured by the
            // first refreshSectionHTML() call above. Non-leader
            // instances refresh themselves via the ajaxProduct:added
            // event dispatched from checkCart / removeIneligibleGifts,
            // but the leader ignores its own dispatched events
            // (see onCartUpdate). Refresh explicitly so the leader's
            // campaign-block UI matches the post-mutation cart state.
            await this.refreshSectionHTML();
          }
        } catch (error) {
          console.error('Error updating campaigns:', error);
          this.showWarningMessage('Failed to update campaigns. Please try again.');
        } finally {
          this.updateInProgress = false;
        }
      }

      /**
       * Fetches the campaigns section and swaps this element's innerHTML
       * to reflect the latest server-rendered state. Captures the
       * pre-swap DOM into prevCampaigns so removeIneligibleGifts can
       * diff against it.
       * @returns {Promise<boolean>} - true if the swap succeeded
       */
      async refreshSectionHTML() {
        const sectionRoute = this.inCart ? routes.cart_url : '/';

        const response = await fetch(`${sectionRoute}?section_id=campaigns`);
        if (!response.ok) throw new Error(`Failed to fetch campaigns: ${response.status}`);

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('campaigns-element');

        if (!sourceQty) {
          console.warn('No campaigns-element found in response');
          return false;
        }

        this.prevCampaigns = this.querySelectorAll('.campaign-block');
        this.innerHTML = sourceQty.innerHTML;
        return true;
      }

      /**
       * Removes gifts that are no longer eligible
       */
      async removeIneligibleGifts() {
        const campaignBlocks = this.querySelectorAll('.campaign-block');
        if (this.prevCampaigns.length > campaignBlocks.length) {
          const prevGiftsArr = Array.from(this.prevCampaigns);
          const prevGiftsIdsData = prevGiftsArr.map((item) => item.dataset.campaignGiftVariantIds);
          const prevGiftsIds =
            prevGiftsIdsData
              .map((item) => item.split(','))
              ?.flat()
              ?.filter((arr) => arr) || [];
          const giftsIdsData = Array.from(campaignBlocks).map((item) => item.dataset.campaignGiftVariantIds);
          const giftIds =
            giftsIdsData
              .map((item) => item.split(','))
              ?.flat()
              ?.filter((arr) => arr) || [];
          const giftIdsSet = new Set(giftIds.map((number) => number));
          const giftIdsToRemove = prevGiftsIds.filter((number) => !giftIdsSet.has(number));
          if (giftIdsToRemove.length == 0) {
            window.campaignsProcessing = false;
            document.querySelectorAll('button[type="submit"][form="cart"], shopify-paypal-button, shopify-apple-pay-button').forEach((button) => {
              button.disabled = false;
              button.removeAttribute('data-pending-validation');
            });
            return true;
          } else {
            const cartData = await this.updateCart(null, 0, giftIdsToRemove);
            const event = new CustomEvent('ajaxProduct:added', {
              detail: { cart: cartData, source: 'campaign', from: this.instanceId }
            });
            document.dispatchEvent(event);
            this.cartUpdateFailure = false;
            return false;
          }
        }
        window.campaignsProcessing = false;
        document.querySelectorAll('button[type="submit"][form="cart"], shopify-paypal-button, shopify-apple-pay-button').forEach((button) => {
          button.disabled = false;
          button.removeAttribute('data-pending-validation');
        });
        return true;
      }

      /**
       * Shows warning message to the user
       * @param {string} message - Optional custom message to display
       */
      showWarningMessage(message) {
        const warningMessage = document.querySelector('.warning-message');
        if (warningMessage) {
          if (message && warningMessage.querySelector('.message-content')) {
            warningMessage.querySelector('.message-content').textContent = message;
          }
          warningMessage.classList.remove('hidden');
        }
      }

      /**
       * Hides warning message
       */
      hideWarningMessage() {
        const warningMessage = document.querySelector('.warning-message');
        if (warningMessage) {
          warningMessage.classList.add('hidden');
        }
      }

      /**
       * Checks cart and updates campaign gifts based on eligibility
       */
      async checkCart() {
        const campaignBlocks = this.querySelectorAll('.campaign-block');
        if (!campaignBlocks.length) return;

        let cartData;
        this.campaigns = {};
        this.activeCampaign = null;
        let cartUpdated = false;

        for (const campaignElem of campaignBlocks) {
          try {
            // If true, user will select prize manually from choices
            const prizeSelection = JSON.parse(campaignElem.dataset.prizeSelection);
            // Campaign name
            const campaignName = campaignElem.dataset.campaignName;
            // Variant id and key of the prize product
            const giftVariantId = campaignElem.dataset.campaignGiftVariantId;

            // How many gift products the user is eligible for
            const campaignEligibleAmount = parseInt(campaignElem.dataset.campaignEligibleAmount);
            // How many campaign prizes are in cart currently
            const prizesInCart = parseInt(campaignElem.dataset.campaignPrizesInCart);
            // How many prize products should be in cart
            let prizeQuantity = campaignEligibleAmount - prizesInCart;

            // Debug
            // console.log(`Campaign: ${campaignName}, Eligible: ${campaignEligibleAmount}, In Cart: ${prizesInCart}, To Add/Remove: ${prizeQuantity}`);

            if (!prizeSelection && prizeQuantity > 0) {
              // If not manual selection, add prize to cart
              cartData = await this.addToCart(giftVariantId, prizeQuantity, campaignName);
              if (cartData) {
                cartUpdated = true;
                // Don't dispatch event for every campaign - do it once at the end
              }
            } else if (prizeQuantity < 0) {
              // If prize quantity is negative, remove prize from cart
              let updatedQuantity = prizesInCart + prizeQuantity;
              if (prizeSelection) {
                updatedQuantity = updatedQuantity - 1 < 0 ? 0 : updatedQuantity - 1;
              }
              cartData = await this.updateCart(giftVariantId, updatedQuantity);
              if (cartData) {
                cartUpdated = true;
                // Don't dispatch event for every campaign - do it once at the end
              }
            }
          } catch (error) {
            console.error(`Error processing campaign block:`, error);
          }
        }

        // Send a single cart update event after processing all campaigns
        if (cartUpdated && cartData) {
          const event = new CustomEvent('ajaxProduct:added', {
            detail: { cart: cartData, source: 'campaign', from: this.instanceId }
          });
          document.dispatchEvent(event);
        }

        return cartData;
      }

      /**
       * Updates cart with new quantities
       * @param {string|null} id - Variant ID to update
       * @param {number} quantity - New quantity
       * @param {Array|null} ids - Array of variant IDs to update to quantity 0
       * @returns {Promise<Object>} - Cart data
       */
      async updateCart(id, quantity, ids = null) {
        let updates;
        if (ids) {
          updates = ids.reduce((a, v) => ({ ...a, [v]: 0 }), {});
        } else {
          updates = {
            [id]: quantity,
          };
        }

        let formData = JSON.stringify({ updates });
        if (this.cart) {
          const sections = ['CartDrawer'];
          const sections_url = window.location.pathname;
          formData = JSON.stringify({ updates, sections, sections_url });
        }

        try {
          const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: formData,
          });

          if (!response.ok) {
            throw new Error(`Cart update failed: ${response.status} ${response.statusText}`);
          }

          this.cartUpdateFailure = false;
          this.hideWarningMessage();
          return await response.json();
        } catch (error) {
          this.cartUpdateFailure = true;
          this.showWarningMessage('Failed to update your cart. Please try again.');
          console.error('Cart update error:', error);
          return null;
        }
      }

      /**
       * Add items to cart
       * @param {string} id - Variant ID to add
       * @param {number} quantity - Quantity to add
       * @param {string} campaign_name - Campaign name
       * @returns {Promise<Object>} - Cart data
       */
      async addToCart(id, quantity, campaign_name) {
        if (!id || quantity <= 0) {
          console.warn('Invalid addToCart parameters', { id, quantity });
          return null;
        }

        const properties = { "_campaign": campaign_name, "_automatic_gift": true };
        const formData = { items: [{ id: id, quantity: quantity, properties }] };

        if (this.cart) {
          formData.sections = ['CartDrawer'];
          formData.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
          });

          if (!response.ok) {
            throw new Error(`Add to cart failed: ${response.status} ${response.statusText}`);
          }

          this.cartUpdateFailure = false;
          this.hideWarningMessage();
          return await response.json();
        } catch (error) {
          this.cartUpdateFailure = true;
          this.showWarningMessage('Failed to add items to your cart. Please try again.');
          console.error('Add to cart error:', error);
          return null;
        }
      }
    }
  );
}
